// Promise-based bridge to /react.harness.html. Each `runReactProblem` call
// sends one `{type:'run'}` message and resolves when the corresponding
// `{type:'result'}` reply arrives. Multiple in-flight runs are tracked by
// `id` so re-runs while a previous run is still resolving don't cross-talk.
//
// The iframe is owned by the caller (React component); this module just
// provides the message-passing semantics.

export interface ReactRunResult {
  passed: boolean;
  errors: string[];
  logs: Array<{ kind: 'log' | 'err'; msg: string }>;
}

export interface ReactPreviewResult {
  error?: string;
}

let nextId = 0;
const pending = new Map<number, (result: ReactRunResult) => void>();
const previewPending = new Map<number, (result: ReactPreviewResult) => void>();

// Global message listener — installed once, dispatches by id to the right
// pending promise. We DON'T install per-call listeners because they'd leak
// if the iframe is unmounted before a run completes.
let listenerInstalled = false;
function ensureListener(): void {
  if (listenerInstalled) return;
  listenerInstalled = true;
  window.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as {
      type?: string;
      id?: number;
      passed?: boolean;
      errors?: string[];
      logs?: ReactRunResult['logs'];
      error?: string;
    };
    if (!data || typeof data.id !== 'number') return;

    if (data.type === 'result') {
      const resolve = pending.get(data.id);
      if (!resolve) return;
      pending.delete(data.id);
      resolve({
        passed: !!data.passed,
        errors: data.errors ?? [],
        logs: data.logs ?? [],
      });
      return;
    }

    if (data.type === 'preview-ready') {
      const resolve = previewPending.get(data.id);
      if (!resolve) return;
      previewPending.delete(data.id);
      resolve({ error: data.error });
      return;
    }
  });
}

/** Wait for the iframe to fire its `{type:'ready'}` message. Resolves
 *  immediately if it already fired before this is called. Times out after
 *  `timeoutMs` (default 10s) — esm.sh + Babel-standalone is ~1-2s typical. */
export function waitForHarnessReady(iframe: HTMLIFrameElement, timeoutMs = 10_000): Promise<void> {
  return new Promise((resolve, reject) => {
    const target = iframe.contentWindow;
    if (!target) {
      reject(new Error('iframe contentWindow is null'));
      return;
    }
    let done = false;
    const timer = window.setTimeout(() => {
      if (done) return;
      done = true;
      window.removeEventListener('message', handler);
      reject(new Error(`React harness did not become ready within ${timeoutMs}ms`));
    }, timeoutMs);
    function handler(event: MessageEvent) {
      if (event.source !== target) return;
      const data = event.data as { type?: string };
      if (data?.type === 'ready') {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        window.removeEventListener('message', handler);
        resolve();
      }
    }
    window.addEventListener('message', handler);
  });
}

/** Send a single run request to the iframe and resolve with its result. */
export function runReactProblem(
  iframe: HTMLIFrameElement,
  userCode: string,
  testScript: string,
): Promise<ReactRunResult> {
  ensureListener();
  const target = iframe.contentWindow;
  if (!target) {
    return Promise.resolve({
      passed: false,
      errors: ['iframe contentWindow is null — harness not mounted'],
      logs: [],
    });
  }
  const id = ++nextId;
  return new Promise<ReactRunResult>((resolve) => {
    pending.set(id, resolve);
    target.postMessage({ type: 'run', id, userCode, testScript }, '*');
  });
}

/** Mount the user's component into the harness iframe's #root without running
 *  any tests. Used by the preview tab — the iframe stays visible and renders
 *  whatever the user is typing (debounced upstream so we don't flood). */
export function sendPreview(
  iframe: HTMLIFrameElement,
  userCode: string,
): Promise<ReactPreviewResult> {
  ensureListener();
  const target = iframe.contentWindow;
  if (!target) {
    return Promise.resolve({ error: 'iframe contentWindow is null — harness not mounted' });
  }
  const id = ++nextId;
  return new Promise<ReactPreviewResult>((resolve) => {
    previewPending.set(id, resolve);
    target.postMessage({ type: 'preview', id, userCode }, '*');
  });
}
